// Top-level error boundary so a single render failure doesn't show the
// user a blank white page. Renders a recoverable card with the error
// message + "Reload" / "Disconnect & start over" actions.

import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("[v2 boundary]", error, info.componentStack);
  }

  private hardReset = (): void => {
    fetch("/oauth/disconnect", { method: "POST", credentials: "include" })
      .catch(() => undefined)
      .finally(() => {
        window.location.href = "/";
      });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f6fafd",
          padding: 24,
        }}
      >
        <div
          style={{
            maxWidth: 520,
            width: "100%",
            background: "white",
            border: "1px solid #d8e2ee",
            borderRadius: 12,
            padding: 28,
            boxShadow: "0 12px 32px -10px rgba(3,45,96,.18)",
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "#ba0517",
              letterSpacing: 0.4,
              textTransform: "uppercase",
              marginBottom: 8,
            }}
          >
            Something went wrong
          </div>
          <h1
            style={{
              fontSize: 20,
              margin: "0 0 12px",
              color: "#032d60",
              fontWeight: 700,
            }}
          >
            The page hit a runtime error
          </h1>
          <pre
            style={{
              fontSize: 12,
              background: "#f4f7fb",
              border: "1px solid #e3eaf3",
              borderRadius: 8,
              padding: 12,
              color: "#032d60",
              overflowX: "auto",
              whiteSpace: "pre-wrap",
              maxHeight: 240,
            }}
          >
            {error.message || String(error)}
          </pre>
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button
              onClick={() => window.location.reload()}
              style={btn("#1d70c9", "white")}
            >
              Reload
            </button>
            <button onClick={this.hardReset} style={btn("white", "#1d70c9")}>
              Disconnect &amp; start over
            </button>
          </div>
        </div>
      </div>
    );
  }
}

function btn(bg: string, fg: string): React.CSSProperties {
  return {
    background: bg,
    color: fg,
    border: bg === "white" ? "1px solid #d8e2ee" : 0,
    padding: "9px 14px",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  };
}
